/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { appTitle } from '@/process.env'
import { accountClient } from '@/services/cloudClient/accountClient'
import { ExtendedPrivateOfferItem, PrivateOfferDetail } from '@/types/privateOffer'
import { Alert, Card, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import '../styles.less'

const { Title, Text } = Typography

interface OfferPricingInfoProps {
  offer: PrivateOfferDetail
}

export function OfferPricingInfo({ offer }: OfferPricingInfoProps) {
  const { t } = useTranslation()
  const [serviceType, setServiceType] = useState<string>('saas') // Default value

  // Table styles
  const tableStyle = {
    fontSize: '14px',
    margin: '0 16px',
    '--ant-table-border-color': 'var(--private-offer-border-color)',
    '--ant-table-header-bg': 'var(--private-offer-table-header-bg)',
    '--ant-table-row-hover-bg': 'var(--private-offer-table-hover-bg)'
  } as React.CSSProperties

  // Get contract information to get serviceType
  useEffect(() => {
    const fetchServiceType = async () => {
      if (offer.contractId) {
        const response = await accountClient.contractsApi.getContract(offer.contractId)
        setServiceType(response.data.serviceType || 'saas')
      }
    }

    fetchServiceType()
  }, [offer.contractId])

  const currencySymbolMap: Record<string, string> = {
    CNY: '￥',
    USD: '$',
    EUR: '€',
    JPY: '¥',
  }

  // Group data by type - 统一使用Compute、Storage、Data Transfer分组
  const groupItemsByType = (items: ExtendedPrivateOfferItem[]) => {
    const groups: Record<string, ExtendedPrivateOfferItem[]> = {}

    items.forEach(item => {
      // 根据 item 的 itemType 字段来判断分组，统一使用标准命名
      let groupKey = ''
      switch (item.itemType?.toLowerCase()) {
        case 'compute':
          groupKey = 'Compute'
          break
        case 'storage':
          groupKey = 'Storage'
          break
        case 'datatransfer':
        case 'data transfer':
        case 'network':
          groupKey = 'Data Transfer'
          break
        default:
          groupKey = item.itemType || 'Other'
          break
      }

      if (!groups[groupKey]) groups[groupKey] = []
      groups[groupKey].push(item)
    })

    return groups
  }

  // SaaS customer price information table column definition
  const getSaasColumns = (): ColumnsType<ExtendedPrivateOfferItem> => {
    // 是否包含折扣列
    const hasDiscountColumn = offer.discountType !== 'tiered'

    // 当包含折扣列时，调整各列宽度，确保总宽度不超出 100%
    // 不包含折扣列时，保持更宽的基础列展示
    const baseColumns = [
      {
        title: t('Cloud Provider'),
        dataIndex: 'cloudProvider',
        key: 'cloudProvider',
        width: hasDiscountColumn ? '22%' : '25%',
        render: (cloudProvider: string) => (
          <span style={{ fontWeight: 500 }}>{cloudProvider}</span>
        ),
      },
      {
        title: t('Cloud Region'),
        dataIndex: 'region',
        key: 'region',
        width: hasDiscountColumn ? '22%' : '25%',
        render: (region: string, record: ExtendedPrivateOfferItem) => (
          <span style={{ fontWeight: 500 }}>{region}</span>
        ),
      },
      {
        title: t('Billing Item'),
        dataIndex: 'billingItem',
        key: 'billingItem',
        width: hasDiscountColumn ? '24%' : '25%',
        render: (billingItem: string) => (
          <span style={{ fontWeight: 500 }}>{billingItem}</span>
        ),
      },
      {
        title: t('Listing Price'),
        dataIndex: 'listingPrice',
        key: 'listingPrice',
        width: hasDiscountColumn ? '15%' : '20%',
        align: 'right' as const,
        render: (price: number) => {
          const currency = offer.currency || ''
          const symbol = currencySymbolMap[currency] || currency || ''
          return price ? `${symbol}${price.toFixed(8)}` : '-'
        },
      },
      {
        title: t('Listing Pricing Unit'),
        dataIndex: 'unit',
        key: 'unit',
        width: hasDiscountColumn ? '15%' : '20%',
        align: 'center' as const,
        render: (unit: string) => unit || '-',
      },
    ]

    // 如果不是Tiered Discount，添加Discount Off列
    if (hasDiscountColumn) {
      baseColumns.push({
        title: t('Discount Rate'),
        dataIndex: 'discount',
        key: 'discount',
        width: '12%',
        align: 'center' as any,
        onCell: () => ({
          style: { whiteSpace: 'nowrap' },
        }),
        render: (discount: number) => {
          // 处理不同的折扣值格式
          let discountValue = discount
          if (discount === undefined || discount === null) {
            discountValue = 1 // 默认无折扣
          } else if (discount > 1) {
            // 如果折扣值大于1，可能是百分比格式（如80表示80%）
            discountValue = discount / 100
          }

          const discountText = `${parseFloat((discountValue * 100).toFixed(2))}%`
          if (discountValue === 1) {
            return '-'
          }
          return (
            <span className="discount-badge" style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              backgroundColor: discountValue < 1 ? 'var(--private-offer-badge-success-bg)' : 'transparent',
              color: discountValue < 1 ? 'var(--private-offer-bg-success)' : 'inherit',
              border: discountValue < 1 ? '1px solid var(--private-offer-badge-success-border)' : 'none',
              display: 'inline-block',
              minWidth: 40,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}>
              {discountText}
            </span>
          )
        },
      } as any)
    }

    return baseColumns as ColumnsType<ExtendedPrivateOfferItem>
  }

  // BYOC customer price information table column definition
  const getByocColumns = (): ColumnsType<ExtendedPrivateOfferItem> => {
    const hasDiscountColumn = offer.discountType !== 'tiered'

    const baseColumns = [
      {
        title: t('CloudProvider'),
        dataIndex: 'cloudProvider',
        key: 'cloudProvider',
        width: hasDiscountColumn ? '18%' : '20%',
      },
      {
        title: t('Instance'),
        dataIndex: 'itemType',
        key: 'itemType',
        width: hasDiscountColumn ? '22%' : '25%',
        render: (itemType: string) => itemType || '-',
      },
      {
        title: t('CPU:Memory'),
        dataIndex: 'billingItem',
        key: 'billingItem_display',
        width: hasDiscountColumn ? '18%' : '20%',
        render: (billingItem: string) => {
          // 显示实例规格名（原 billingItem）
          if (billingItem?.includes('aws.r')) {
            return 'aws.rxx serials'
          } else if (billingItem?.includes('aws.m')) {
            return 'aws.mxx serials'
          }
          return billingItem || '-'
        },
      },
      {
        title: t('Credit/Core/hour'),
        dataIndex: 'creditPerCoreHour',
        key: 'creditPerCoreHour',
        width: hasDiscountColumn ? '15%' : '18%',
        align: 'right' as const,
        render: (creditPerCoreHour: number, record: ExtendedPrivateOfferItem) => {
          // 根据实例类型显示默认值
          const billingItem = record.billingItem || ''
          if (billingItem.includes('aws.r')) {
            return '0.125'
          } else if (billingItem.includes('aws.m')) {
            return '0.1'
          }
          return creditPerCoreHour ? creditPerCoreHour.toFixed(8) : '-'
        },
      },
      {
        title: t('Price/Credit (Before discount)'),
        dataIndex: 'price',
        key: 'price',
        width: hasDiscountColumn ? '12%' : '15%',
        align: 'right' as const,
        render: (price: number) => {
          const currency = offer.currency || ''
          const symbol = currencySymbolMap[currency] || currency || ''
          // AWS实例默认价格为$1.00
          const defaultPrice = price || 1.00
          return `${symbol}${defaultPrice.toFixed(8)}`
        },
      },
      {
        title: t('Listing Pricing Unit'),
        dataIndex: 'unit',
        key: 'unit',
        width: hasDiscountColumn ? '15%' : '17%',
        align: 'center' as const,
        render: (unit: string) => unit || '-',
      },
    ]

    // 如果不是Tiered Discount，添加Discount Off列
    if (hasDiscountColumn) {
      baseColumns.push({
        title: t('Discount Rate'),
        dataIndex: 'discount',
        key: 'discount',
        width: '10%',
        align: 'center' as any,
        onCell: () => ({
          style: { whiteSpace: 'nowrap' },
        }),
        render: (discount: number) => {
          // 处理不同的折扣值格式
          let discountValue = discount
          if (discount === undefined || discount === null) {
            discountValue = 1 // 默认无折扣
          } else if (discount > 1) {
            // 如果折扣值大于1，可能是百分比格式（如80表示80%）
            discountValue = discount / 100
          }

          const discountText = `${parseFloat((discountValue * 100).toFixed(2))}%`
          if (discountValue === 1) {
            return '-'
          }
          return (
            <span className="discount-badge" style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              backgroundColor: discountValue === 1 ? 'transparent' : 'var(--private-offer-alert-info-bg)',
              color: discountValue === 1 ? 'inherit' : 'var(--private-offer-bg-primary)',
              border: discountValue === 1 ? 'none' : '1px solid var(--private-offer-alert-info-border)',
              display: 'inline-block',
              whiteSpace: 'nowrap',
            }}>
              {discountText}
            </span>
          )
        },
      } as any)
    }

    return baseColumns as ColumnsType<ExtendedPrivateOfferItem>
  }

  // Render SaaS pricing information
  const renderSaasPricing = () => {
    const groups = groupItemsByType(offer.offerItems || [])

    // 定义计费类型的优先级顺序
    const types = ['Compute', 'Storage', 'Data Transfer']
    const allTypes = [...types, ...Object.keys(groups).filter(type => !types.includes(type))]

    return (
      <>
        {/* Show tiered discount first if applicable */}
        {renderTieredDiscountInfo()}

        {/* Display total items summary */}
        {/* <div style={{ margin: '0 16px 16px 32px' }}>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            Total items: {offer.offerItems?.length || 0} | Grouped by billing type for clarity
          </Typography.Paragraph>
        </div> */}

        {/* Display tables grouped by category */}
        {allTypes.map(type => {
          const items = groups[type] || []
          if (items.length === 0) return null

          return (
            <div key={type} style={{ marginBottom: 24, margin: '0 16px' }}>
              <Title level={5} className="section-title" style={{
                marginBottom: 16,
                borderBottom: '1px solid var(--ant-color-border)',
                paddingBottom: 4
              }}>
                {type} ({items.length} item{items.length > 1 ? 's' : ''})
              </Title>
              <Table
                columns={getSaasColumns()}
                dataSource={items}
                pagination={false}
                size="small"
                rowKey="id"
                bordered
                style={tableStyle}
                scroll={{ x: 'max-content' }}
                className="pricing-table"
              />
            </div>
          )
        })}
      </>
    )
  }

  // Render Tiered Discount Information
  const renderTieredDiscountInfo = () => {
    if (offer.discountType !== 'tiered' || !offer.tieredDiscounts || offer.tieredDiscounts.length === 0) {
      return null
    }

    // Determine the service type based on offer items
    const hasCreditsPerCoreHour = offer.offerItems?.some(item => item.creditPerCoreHour != null)
    const isByocService = hasCreditsPerCoreHour

    // Set appropriate threshold unit based on service type
    const thresholdUnit = isByocService ? 'Credits' : (currencySymbolMap[offer.currency || ''] || '$')

    const tieredColumns: ColumnsType<any> = [
      {
        title: t('Tier Level'),
        dataIndex: 'tierLevel',
        key: 'tierLevel',
        width: '20%',
        align: 'center',
        render: (tierLevel: number) => (
          <Tag
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              backgroundColor: 'var(--private-offer-bg-primary)',
              color: '#ffffff',
              border: 'none'
            }}
          >
            Tier {tierLevel}
          </Tag>
        ),
      },
      {
        title: t('Consumption Range'),
        key: 'range',
        width: '50%',
        render: (_, record: any) => {
          const minThreshold = record.minThreshold || 0
          const maxThreshold = record.maxThreshold

          if (maxThreshold === -1) {
            return (
              <Text>
                {isByocService
                  ? `${minThreshold.toLocaleString()} Credits`
                  : `${thresholdUnit}${minThreshold.toLocaleString()}`
                } {t('and above')}
              </Text>
            )
          } else {
            return (
              <Text>
                {isByocService
                  ? `${minThreshold.toLocaleString()} Credits - ${maxThreshold.toLocaleString()} Credits`
                  : `${thresholdUnit}${minThreshold.toLocaleString()} - ${thresholdUnit}${maxThreshold.toLocaleString()}`
                }
              </Text>
            )
          }
        },
      },
      {
        title: t('Discount Rate'),
        dataIndex: 'discountRate',
        key: 'discountRate',
        width: '30%',
        align: 'center',
        render: (discountRate: number) => {
          // 所有服务类型：直接使用折扣值，无需反转处理
          let discountPercentage: string

          if (discountRate === undefined || discountRate === null) {
            discountPercentage = '0.0' // 默认无折扣
          } else {
            // 直接使用折扣值作为百分比
            discountPercentage = (discountRate * 100).toFixed(1)
          }
          return (
            <Tag
              style={{
                fontSize: '12px',
                fontWeight: 'bold',
                minWidth: '60px',
                textAlign: 'center',
                backgroundColor: discountRate > 0 ? 'var(--private-offer-bg-success)' : 'var(--private-offer-text-secondary)',
                color: '#ffffff',
                border: 'none'
              }}
            >
              {discountPercentage}%
            </Tag>
          )
        },
      },
    ]

    return (
      <div style={{ marginBottom: 24, margin: '0 16px' }}>
        <Title level={5} className="section-title" style={{ marginBottom: 16 }}>
          {t('Tiered Discount')}
        </Title>
        {/* BYOC类型不显示此文案 */}
        {!isByocService && (
          <Alert
            message={t('Volume-based discount tiers apply to your total monthly consumption')}
            type="info"
            showIcon={false}
            style={{ marginBottom: 16, fontSize: '12px' }}
          />
        )}
        <Table
          columns={tieredColumns}
          dataSource={offer.tieredDiscounts}
          pagination={false}
          size="small"
          rowKey="tierLevel"
          bordered
          style={tableStyle}
          scroll={{ x: 'max-content' }}
          className="pricing-table tiered-discount-table"
        />
        {/* BYOC类型不显示Note文案 */}
        {!isByocService && (
          <Alert
            message={
              <>
                <strong>{t('Note')}:</strong> {t('Higher consumption automatically gets better rates. Discount applies to the entire monthly bill.')}
              </>
            }
            type="info"
            showIcon={false}
            style={{ marginTop: 12, fontSize: '12px' }}
          />
        )}
      </div>
    )
  }

  // Render BYOC pricing information
  const renderByocPricing = () => {
    return (
      <>
        {/* Show tiered discount first if applicable */}
        {renderTieredDiscountInfo()}

        {/* Actual pricing items */}
        {offer.offerItems && offer.offerItems.length > 0 && (
          <div style={{ marginBottom: 24, margin: '0 16px' }}>
            <Title level={5} className="section-title" style={{ marginBottom: 16 }}>
              {offer.discountType === 'tiered' ? t('Billing Items') : t('Pricing & Discount Off')}
            </Title>
            <Table
              columns={getByocColumns()}
              dataSource={offer.offerItems}
              pagination={false}
              size="small"
              rowKey="id"
              bordered
              style={tableStyle}
              scroll={{ x: 'max-content' }}
              className="pricing-table"
            />
          </div>
        )}
      </>
    )
  }

  return (
    <div className="private-offer-container">
      <Card>

        {/* Explanation text - 简化显示，BYOC类型不显示官网链接文案 */}
        {offer.status !== 'accepted' && serviceType !== 'byoc' && (
          <Alert
            message={
              <>
                {t('The private offer confirms the discount. Final prices are based on the official website. Please refer to the')}{' '}
                <a href={`https://${appTitle}.io/pricing/details/`} target="_blank" rel="noopener noreferrer">
                  {t('pricing page')}
                </a>{' '}
                {t('for current listing prices.')}
              </>
            }
            type="info"
            showIcon={false}
            style={{ marginBottom: 12, fontSize: '12px' }}
          />
        )}

        {serviceType === 'byoc' ? renderByocPricing() : renderSaasPricing()}
      </Card>
    </div>
  )
}